import { getBearing, getDistance, getMeanTime } from "src/app/module/aero-navic/aero-navic.module";
import { getBearing360 } from "src/app/module/x-math/x-math.module";

export class RouteService {
  readonly waypointRadius: number = 750;
  private route: RouteInterface = {
    points: [],
    created: -1,
    destination: {
      name: ["", ""],
      isDisplaced: false,
      distance: 0,
    },
    id: -1,
    name: "",
  };

  constructor(points: PointInterface[], name: string[]) {
    const d = new Date();
    this.route.points = points;
    this.route.created = d.getTime();
    this.route.id = d.getTime();
    this.route.destination.name = name;
  }

  public setId(id: number): void {
    this.route.id = id;
  }

  public setCreated(created: number): void {
    this.route.created = created;
  }

  public replaceWith(points: PointInterface[]): void {
    this.route.points = points;
  }

  public getInfoForNextActivePoint(ppos: number[], speed: number): { heading: number; distance: number; time: number } {
    try {
      for (let i = 0; i < this.route.points.length; i++) {
        if (this.route.points[i].active || i === this.route.points.length - 1) {
          const heading = Math.round(getBearing360(getBearing(ppos, this.route.points[i].point)));
          const distance = getDistance(ppos, this.route.points[i].point);
          let time = getMeanTime(distance, speed);
          if (speed < 0.1) {
            time = getMeanTime(distance, 61.74);
          }
          return {
            heading,
            distance,
            time,
          };
        }
      }
    } catch (err) {
      return {
        heading: null,
        distance: null,
        time: null,
      };
    }
  }

  public getInfoForDest(ppos: number[], speed: number): { distance: number; time: number } {
    const distance = [0];

    try {
      if (this.route.points.length === 0) {
        return {
          distance: null,
          time: null,
        };
      } else {
        for (let i = 0; i < this.route.points.length; i++) {
          if (
            (this.route.points[i].active && i == 0) ||
            (i > 0 && !this.route.points[i - 1].active && this.route.points[i].active)
          ) {
            distance.push(getDistance(ppos, this.route.points[i].point));
          } else if (this.route.points[i].active && i > 0) {
            distance.push(getDistance(this.route.points[i - 1].point, this.route.points[i].point));
          }
        }

        if (distance.length > 1) {
          const dist = distance.reduce((a, b) => {
            return a + b;
          });

          if (speed < 0.1) {
            return {
              distance: dist,
              time: getMeanTime(dist, 61.74),
            };
          } else {
            return {
              distance: dist,
              time: getMeanTime(dist, speed),
            };
          }
        }
      }
    } catch (err) {
      return {
        distance: null,
        time: null,
      };
    }
  }

  public addPoint(point: PointInterface): void {
    if (point.displayName.indexOf("{x}") !== -1) {
      point.displayName = point.displayName.replace("{x}", (this.route.points.length + 1).toString());
    }
    this.route.points.push(point);
  }

  public getAllPoints(): PointInterface[] {
    return this.route.points;
  }

  public addPointAfter(point: PointInterface, index: number): void {
    if (this.route.points.length > 0) {
      this.route.points.splice(index, 0, point);
    } else {
      this.route.points.push(point);
    }
  }

  public cleanRoute(): void {
    if (this.route.points.length > 0) {
      this.route.points.forEach((point, index) => {
        const name = point.displayName.match(/wpt|dest/i);
        if (name !== null && name.length > 0) {
          point.displayName = point.name;
        }
      });
      if (this.route.points[this.route.points.length - 1].displayName.search(/home|mob/i) === -1) {
        // this.route.points[this.route.points.length - 1].displayName = "DEST";
      }
    }
  }

  public smartPointInsert(point: PointInterface, ppos: PointInterface): void {
    const distances = [];
    const points = [ppos].concat(this.route.points);
    if (this.route.points.length === 0) {
      this.route.points.push(point);
    } else if (this.route.points.length === 1) {
      this.route.points.unshift(point);
    } else if (this.route.points.length === 2) {
      this.route.points.splice(1, 0, point);
    } else {
      for (let j = 1; j < points.length; j++) {
        distances[j - 1] = 0;
        for (let i = 1; i < points.length; i++) {
          if (i == j) {
            distances[j - 1] += getDistance(points[i - 1].point, point.point);
            distances[j - 1] += getDistance(point.point, points[i].point);
          } else {
            distances[j - 1] += getDistance(points[i - 1].point, points[i].point);
          }
        }
      }

      let index = distances.indexOf(Math.min.apply(null, distances));

      // index 0 makes no sence whatsoever
      if (index == 0) {
        index = 1;
      }
      this.route.points.splice(index, 0, point);
    }
  }

  public removePoint(index: number): void {
    this.route.points.splice(index, 1);
  }
  public overflyPoint(index: number): void {
    for (let i = 0; i <= index; i++) {
      try {
        if (this.route.points[i].active) {
          // never overfly desintation
          if (i !== this.route.points.length - 1) {
            this.route.points[i].active = false;
          }

          break;
        }
      } catch (err) {
        console.error("point index exceeds number of points..");
      }
    }
  }

  public removeLastPoint(): void {
    this.route.points.pop();
  }

  public removeAllPoints(): void {
    this.route.points = [];
  }

  public setName(name: string): void {
    this.route.name = name;
  }

  public getName(): string {
    return this.route.name;
  }

  public setDestination(destination: { name: string[]; isDisplaced: boolean; distance: number }): void {
    this.route.destination = destination;
  }

  public getDestination(): {
    name: string[];
    isDisplaced: boolean;
    distance: number;
  } {
    if (this.route.points.length > 0) {
      return this.route.destination;
    } else {
      return {
        name: ["", ""],
        isDisplaced: false,
        distance: 0,
      };
    }
  }

  public setNextPointInactive(): void {
    for (let i = 0; i < this.route.points.length; i++) {
      if (this.route.points[i].active) {
        this.route.points[i].active = false;
        break;
      }
    }
  }

  public getRoute(): RouteInterface {
    return this.route;
  }
}

export interface RouteInterface {
  points: PointInterface[];
  destination: {
    name: string[];
    isDisplaced: boolean;
    distance: number;
  };
  created: number;
  id: number;
  name: string;
}

export interface PointInterface {
  point: [number, number];
  name: string;
  displayName: string;
  id: number;
  active: boolean;
}
